import type { LeaveRequest } from "@spork/s4p-server-sdk";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { toastError, toastSuccess } from "@/components/AppToast";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { canParentCancelLeaveRequest } from "@/features/home/leaveRequestStatus";
import { LeaveRequestAttachmentsPanel } from "@/features/requests/LeaveRequestAttachmentsPanel";
import { useDeferredAttachmentStaffNotify } from "@/features/requests/useDeferredAttachmentStaffNotify";
import {
    getChildLabel,
    showChildLabels,
    useChildren,
} from "@/features/selectedChild";
import { showAlert } from "@/lib/showAlert";
import { log } from "@/logger";
import { useCancelLeaveRequestMutation } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

import { LeaveRequestCollapsedActions } from "./LeaveRequestCollapsedActions";
import { LeaveRequestSummary } from "./LeaveRequestListItem";
import { LeaveRequestProgressTimeline } from "./LeaveRequestProgressTimeline";

type LeaveRequestDetailScreenProps = {
    request: LeaveRequest;
};

function getErrorMessage(error: unknown, fallback: string): string {
    if (error && typeof error === "object" && "message" in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === "string" && message.trim()) {
            return message;
        }
    }

    return fallback;
}

export function LeaveRequestDetailScreen({
    request,
}: LeaveRequestDetailScreenProps) {
    const { t } = useTranslation();
    const { children } = useChildren();
    const [cancelLeaveRequest, { isLoading: isCancelling }] =
        useCancelLeaveRequestMutation();
    const deferredAttachments = useDeferredAttachmentStaffNotify();

    const childLabel = useMemo(() => {
        if (!showChildLabels(children.length)) {
            return undefined;
        }

        const child = children.find((item) => item.id === request.child_id);
        return child ? getChildLabel(child) : undefined;
    }, [children, request.child_id]);

    const canCancel = canParentCancelLeaveRequest(request.status);

    function handleCancel() {
        showAlert(
            t("leave.cancelConfirmTitle"),
            t("leave.cancelConfirmMessage"),
            [
                { text: t("leave.cancelConfirmKeep"), variant: "outline" },
                {
                    text: t("leave.cancelRequest"),
                    variant: "destructive",
                    testID: TEST_IDS[TEST_IDS.REQUESTS_CANCEL_CONFIRM_BUTTON],
                    onPress: async () => {
                        try {
                            await cancelLeaveRequest(request.id).unwrap();
                            toastSuccess(
                                t("leave.cancelledTitle"),
                                t("leave.cancelledBody"),
                            );
                        } catch (error) {
                            log.error("cancelLeaveRequest failed", error);
                            toastError(
                                t("leave.errorTitle"),
                                getErrorMessage(error, t("leave.cancelFailed")),
                            );
                        }
                    },
                },
            ],
        );
    }

    return (
        <SafeAreaView className="flex-1 bg-background" edges={["bottom"]}>
            <ScrollView
                className="flex-1"
                contentContainerClassName="px-4 pb-6 web:mx-auto web:w-full web:max-w-lg"
                testID={TEST_IDS[TEST_IDS.LEAVE_REQUEST_DETAIL_SCREEN]}
            >
                <View className="rounded-card border border-border bg-card px-4 shadow-none">
                    <View className="flex-row items-start justify-between gap-4 py-4">
                        <LeaveRequestSummary
                            request={request}
                            childLabel={childLabel}
                        />
                    </View>

                    <LeaveRequestCollapsedActions
                        request={request}
                        deferredAttachments={deferredAttachments}
                    />

                    <View className="gap-3 pb-4">
                        {request.message ? (
                            <Text>{request.message}</Text>
                        ) : null}

                        <LeaveRequestAttachmentsPanel
                            request={request}
                            onAdded={deferredAttachments.rememberAdded}
                            onRemoved={deferredAttachments.forgetRemoved}
                            onBusyChange={
                                deferredAttachments.onAttachmentsBusyChange
                            }
                            onPickerSessionChange={
                                deferredAttachments.onPickerSessionChange
                            }
                        />

                        <View className="rounded-card bg-background p-4 shadow-none gap-2">
                            <Text variant="label">{t("leave.history")}</Text>
                            <LeaveRequestProgressTimeline request={request} />
                        </View>
                        {canCancel ? (
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={isCancelling}
                                onPress={handleCancel}
                                testID={
                                    TEST_IDS[
                                        TEST_IDS.REQUESTS_LIST_ITEM_CANCEL_BUTTON
                                    ]
                                }
                            >
                                <Text className="text-destructive">
                                    {isCancelling
                                        ? t("leave.cancelling")
                                        : t("leave.cancelRequest")}
                                </Text>
                            </Button>
                        ) : null}
                    </View>
                </View>
            </ScrollView>
        </SafeAreaView>
    );
}
